import lodash from 'lodash'
import store from '@/store'
import i18n, { langs } from '@/utils/i18n'
import { WORK_PERMIT_TYPES } from './consts'

export const REGIONAL_WORK_PERMIT_LABELS_PATH = 'regionalLabels.workPermit'

const DEFAULT_REGION = 'HK'

const BUILTIN_LABELS = {
  [langs.enUS]: {
    HK: {
      hoisting: 'Hoisting',
      fireMaintenanceBadgeHoisting: 'Fire Maintenance Badge Hoisting',
      greenCard: 'Green Card',
    },
    SG: {
      hoisting: 'Hot work permit',
      fireMaintenanceBadgeHoisting: 'Fire Alarm Isolation',
      greenCard: 'NRIC No/ Work permit ID No',
    },
  },
  [langs.zhCN]: {
    HK: {
      hoisting: '吊装/高空作业',
      fireMaintenanceBadgeHoisting: '消防吊装/高空作业',
      greenCard: '身份证',
    },
    SG: {
      hoisting: '吊装/高空作业',
      fireMaintenanceBadgeHoisting: '消防吊装/高空作业',
      greenCard: '身份证',
    },
  },
}

function currentRegionName () {
  const getters = lodash.get(store, 'getters', {})
  return getters['regions/regionName'] ||
    lodash.get(getters['regions/currentRegion'], 'name') ||
    DEFAULT_REGION
}

function normalizeRegionName (regionName) {
  return String(regionName || currentRegionName() || DEFAULT_REGION).toUpperCase()
}

function normalizeLanguage (language) {
  return language || i18n.locale || langs.enUS
}

function getBuiltinLabel (key, regionName, language) {
  const languageLabels = BUILTIN_LABELS[normalizeLanguage(language)] || BUILTIN_LABELS[langs.enUS]
  const regionLabels = languageLabels[normalizeRegionName(regionName)] || languageLabels[DEFAULT_REGION] || {}
  return regionLabels[key] || lodash.get(BUILTIN_LABELS, [langs.enUS, DEFAULT_REGION, key], '')
}

export function getRegionalWorkPermitLabel (key, regionName, language) {
  const region = normalizeRegionName(regionName)
  const lang = normalizeLanguage(language)
  const regionPath = `${REGIONAL_WORK_PERMIT_LABELS_PATH}.${key}.${region}`
  if (i18n.te(regionPath, lang)) {
    const value = i18n.t(regionPath, lang)
    if (value) return value
  }

  const hkPath = `${REGIONAL_WORK_PERMIT_LABELS_PATH}.${key}.${DEFAULT_REGION}`
  if (i18n.te(hkPath, lang)) {
    const value = i18n.t(hkPath, lang)
    if (value) return value
  }

  return getBuiltinLabel(key, region, lang)
}

function isZh (language) {
  return normalizeLanguage(language).toLowerCase() === langs.zhCN.toLowerCase()
}

function buildGreenCardNoLabel (greenCardLabel, language) {
  if (isZh(language)) {
    return greenCardLabel === '身份证' ? '身份证信息' : greenCardLabel
  }
  if (/\b(no\.?|id)\b/i.test(greenCardLabel)) {
    return greenCardLabel
  }
  return `${greenCardLabel} No.`
}

function buildGreenCardExpiryDateLabel (greenCardLabel, language) {
  return isZh(language)
    ? `${greenCardLabel}有效期限`
    : `${greenCardLabel} Expiry Date`
}

function includesPermitWord (label, language) {
  return !isZh(language) && /\bpermits?\b/i.test(label)
}

function buildHoistingPermitLabel (hoistingLabel, language, plural = false) {
  if (isZh(language)) return `${hoistingLabel}许可证`
  if (includesPermitWord(hoistingLabel, language)) {
    return plural ? hoistingLabel.replace(/\bpermit\b/i, (match) => `${match}(s)`) : hoistingLabel
  }
  return plural ? `${hoistingLabel} Permit(s)` : `${hoistingLabel} Permit`
}

export function getRegionalWorkPermitLabels (regionName, language) {
  const labels = {
    hoisting: getRegionalWorkPermitLabel('hoisting', regionName, language),
    fireMaintenanceBadgeHoisting: getRegionalWorkPermitLabel('fireMaintenanceBadgeHoisting', regionName, language),
    greenCard: getRegionalWorkPermitLabel('greenCard', regionName, language),
  }
  return {
    ...labels,
    hoistingPermit: buildHoistingPermitLabel(labels.hoisting, language),
    hoistingPermits: buildHoistingPermitLabel(labels.hoisting, language, true),
    greenCardNo: buildGreenCardNoLabel(labels.greenCard, language),
    greenCardExpiryDate: buildGreenCardExpiryDateLabel(labels.greenCard, language),
  }
}

export function getWorkPermitTypeLabel (type, regionName, language) {
  if (type === WORK_PERMIT_TYPES.HOISTING) {
    return getRegionalWorkPermitLabel('hoisting', regionName, language)
  }
  return i18n.t(`workPermit.types.${type}`)
}

export function getHoistingAndHotWorkLabel (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language) ? `${hoisting}&动火作业` : `${hoisting} & Hot Work`
}

export function getAddHoistingLabel (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language) ? `添加${hoisting}申请+` : `Add ${hoisting} +`
}

export function getThreeDaysHoistingLabel (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language) ? `三日内的${hoisting}申请` : `${hoisting} in 3 Days`
}

export function getPermitsAtLocationLabel (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  if (isZh(language)) return `${hoisting}位置`
  return `${hoisting} at Location`
}

export function getDurationTitleLabel (regionName, language) {
  const fireMaintenanceBadgeHoisting = getRegionalWorkPermitLabel('fireMaintenanceBadgeHoisting', regionName, language)
  return isZh(language)
    ? `${fireMaintenanceBadgeHoisting}证（24小时内）`
    : `Duration of ${fireMaintenanceBadgeHoisting} (within 24 hrs)`
}

export function getHoistingConfirmHint (action, regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language)
    ? `您是否确认 ${action} 此${hoisting}申请？`
    : `Are you sure you want to ${action} this ${hoisting}?`
}

export function getHoistingNoShowHint (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language)
    ? `您是否确认更改此${hoisting}为"过期"？ `
    : `Are you sure you want to change this ${hoisting} to "EXPIRED"? `
}

export function getDeleteRejectedWPHint (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language)
    ? `您是否确认删除此施工许可证？\n与此施工许可证的${hoisting}也将被删除（如有）。`
    : `Are you sure you want to delete this Work Permit?\n${hoisting}(s) associated with this Work Permit will also be deleted (if any).`
}

export function getTooManyHoistingMessage (maxWAPPRHoistingNum, limitType, regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language)
    ? `每个项目每层楼只能申请 ${maxWAPPRHoistingNum} 份${hoisting}申请。请删除多余的${hoisting}申请。`
    : `You can only apply ${maxWAPPRHoistingNum} ${hoisting} application requests (per floor for each ${limitType}) at any one time. Please remove the extra ${hoisting}(s).`
}

export function getHoistingNextReviewerTitle (regionName, language) {
  const hoisting = getRegionalWorkPermitLabel('hoisting', regionName, language)
  return isZh(language) ? `请选择下一位${hoisting}审核人` : `Please select ${hoisting} next reviewer`
}
